import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { BIRDS, LOGO } from "@/lib/brand";
import { useLang } from "@/lib/i18n";

function Counter({ to = 180, suffix = "%", duration = 2.2, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, to, duration]);

  return (
    <span ref={ref} data-testid={testId} className="tabular-nums">
      {val}
      {suffix}
    </span>
  );
}

export default function Legacy() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  return (
    <section
      id="legacy"
      ref={ref}
      data-testid="legacy-section"
      className="relative bg-[#F4F4F0] text-black border-t border-black"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5 md:col-start-1 flex flex-col">
          <div className="flex items-center gap-4 mb-8">
            <span className="w-10 h-px bg-black" />
            <span className="font-mono text-[10px] tracking-[0.35em] uppercase">
              {t("legacy.chapter")}
            </span>
          </div>

          <motion.h2
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight"
          >
            {t("legacy.title_1")} <br />
            <em className="italic">{t("legacy.title_2_italic")}</em> <br />
            {t("legacy.title_3")}
          </motion.h2>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-10 text-base md:text-lg leading-relaxed text-black/80 max-w-[52ch]"
          >
            {t("legacy.body")}
          </motion.p>

          <div className="mt-16 grid grid-cols-2 gap-8 items-start">
            <div className="border-t border-black pt-4 relative">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
                {t("legacy.print_excellence")}
              </div>
              <motion.img
                src={LOGO.badge_32}
                alt="32 Years of Printing Excellence"
                initial={{ opacity: 0, scale: 0.7, rotate: -20 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true, margin: "-15% 0px" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="mt-3 w-36 md:w-44 select-none"
                data-testid="legacy-badge-32"
              />
              <div className="text-sm text-black/70 mt-3 max-w-[28ch]">
                {t("legacy.years_body")}{" "}
                <span className="hidden" data-testid="legacy-counter-years">
                  <Counter to={32} suffix="Y" duration={1.6} />
                </span>
              </div>
            </div>
            <div className="border-t border-black pt-4">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
                {t("legacy.digital_division")}
              </div>
              <div className="font-display text-6xl md:text-7xl mt-2 text-[color:var(--ap-magenta)] leading-none">
                <Counter to={180} suffix="%" duration={2.2} testId="legacy-counter-growth" />
              </div>
              <div className="text-sm text-black/70 mt-3 max-w-[28ch]">
                {t("legacy.growth_body")}
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-6 md:col-start-7 relative">
          <div className="relative w-full aspect-[3/4] md:aspect-[4/5] overflow-hidden border border-black bg-black">
            <motion.img
              style={{ y, scale }}
              src="https://images.unsplash.com/photo-1693031630157-7ecc8d06de63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxtYWNybyUyMG9mZnNldCUyMHByaW50aW5nJTIwbWFjaGluZSUyMHJvbGxlciUyMGlua3xlbnwwfHx8fDE3ODU3NzQzMjV8MA&ixlib=rb-4.1.0&q=85"
              alt="Macro offset printing roller"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black/80 via-black/10 to-transparent text-[#F4F4F0]">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-80">
                Plate → Blanket → Impression
              </div>
              <div className="font-display italic text-2xl md:text-3xl mt-2">
                {t("legacy.plate_quote")}
              </div>
            </div>
          </div>
          <img
            src={BIRDS.bird_flock}
            alt=""
            aria-hidden
            className="pointer-events-none hidden md:block absolute -left-16 -bottom-10 w-56 object-contain opacity-95"
          />
        </div>
      </div>
    </section>
  );
}
